import { useCallback, useRef, useState } from "react";
import axios from "axios";
import { API_DOMAIN } from "config";

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 60000;

const emptyPaymentMethod = {
  has_payment_method: false,
  payment_method: null,
};

function hasActivePaymentMethod(data) {
  return !!(data?.has_payment_method || data?.payment_method);
}

function normalizeStatus(data) {
  return {
    status: data?.status || "",
    payment_method: data?.payment_method || null,
    has_callback_payload: data?.has_callback_payload,
    callback_reason: data?.callback_reason || "",
  };
}

export default function useDealerPaymentMethodSetup() {
  const [loading, setLoading] = useState(false);
  const [setup, setSetup] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    intervalRef.current = null;
    timeoutRef.current = null;
  }, []);

  const reset = useCallback(() => {
    stopPolling();
    setLoading(false);
    setSetup(null);
    setStatus("idle");
    setError("");
  }, [stopPolling]);

  const checkPaymentMethod = useCallback(async (userId) => {
    if (!userId) return emptyPaymentMethod;

    const res = await axios.get(`${API_DOMAIN}/billing/dealerPaymentMethod/${userId}`);
    const data = res?.data?.data || emptyPaymentMethod;

    return {
      ...data,
      has_payment_method: hasActivePaymentMethod(data),
    };
  }, []);

  const createCardSetup = useCallback(async (userId) => {
    if (!userId) {
      throw new Error("missing_user_id");
    }

    setLoading(true);
    setStatus("creating");
    setError("");

    try {
      const res = await axios.post(`${API_DOMAIN}/billing/createDealerCardSetup`, {
        user_id: userId,
      });
      const data = res?.data?.data;

      if (!res?.data?.success || !data?.setup_id || !data?.sale_url) {
        throw new Error("missing_setup_data");
      }

      setSetup(data);
      setStatus("iframe");
      return data;
    } catch (err) {
      setStatus("error");
      setError("לא הצלחנו לפתוח שמירת כרטיס. נסו שוב.");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const pollCardSetupStatus = useCallback(
    (setupId) => {
      if (!setupId) {
        return Promise.reject(new Error("missing_setup_id"));
      }

      stopPolling();
      setStatus("processing");
      setError("");

      return new Promise((resolve, reject) => {
        const finish = (result, isError = false) => {
          stopPolling();
          if (isError) {
            reject(result);
          } else {
            resolve(result);
          }
        };

        const checkStatus = async () => {
          try {
            const res = await axios.get(`${API_DOMAIN}/billing/dealerCardSetupStatus/${setupId}`);
            const data = normalizeStatus(res?.data?.data);

            if (data.status === "completed" && data.payment_method) {
              setStatus("success");
              finish(data);
              return;
            }

            if (
              data.status === "failed" ||
              data.callback_reason === "TOKEN_VALIDATION_FAILED" ||
              data.callback_reason === "CARD_TOKEN_MISSING"
            ) {
              setStatus("error");
              setError("שמירת הכרטיס לא הושלמה. אפשר לנסות שוב.");
              finish(data, true);
            }
          } catch (err) {
            // PayMe callback can arrive a moment after the return page, so polling continues.
          }
        };

        checkStatus();
        intervalRef.current = setInterval(checkStatus, POLL_INTERVAL_MS);
        timeoutRef.current = setTimeout(() => {
          setStatus("timeout");
          setError("הכרטיס נקלט ב-PayMe, אך האישור עדיין לא הגיע לשרת. נסו לבדוק שוב בעוד רגע.");
          finish({ status: "timeout" }, true);
        }, POLL_TIMEOUT_MS);
      });
    },
    [stopPolling]
  );

  return {
    loading,
    setup,
    status,
    error,
    checkPaymentMethod,
    createCardSetup,
    pollCardSetupStatus,
    reset,
    setStatus,
    setError,
  };
}
